import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { hrApi } from '../../services/hrApi.js';
import { usersApi } from '../../services/usersApi.js';

/**
 * Ein Anmeldekonto mit einer Personalakte verbinden.
 *
 * Warum das eine eigene Maske wert ist: ohne diese Verbindung ist die gesamte
 * Selbstbedienung tot. `/api/hr/me` löst die Person aus dem geprüften Token
 * über `employees.user_id` auf; ist die Spalte leer, antwortet der Dienst mit
 * 403 und die Seite "Meine Zeit" zeigt nur einen Hinweis. Die Route dafür gab
 * es im Dienst von Anfang an - in der Oberfläche führte kein einziger Weg
 * dorthin, und deshalb war das Feld in JEDER Personalakte leer.
 *
 * Belegte Konten stehen mit in der Liste, aber abgewählt: ein Konto gehört
 * zu höchstens einer Akte (employees_user_id_key). Sie ganz wegzulassen wäre
 * schlechter - dann suchte man eine Adresse, die man mit eigenen Augen im
 * System gesehen hat, und fände sie nicht.
   */
export function LinkUserModal({ employee, belegt, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [userId, setUserId] = useState('');
  const [error, setError] = useState(null);

  const { data: konten, isLoading, error: ladeFehler } = useQuery({
    queryKey: ['users', 'list'],
    queryFn: () => usersApi.list(),
    enabled: Boolean(employee),
  });

  useEffect(() => {
    if (employee) {
      setUserId(employee.userId || '');
      setError(null);
    }
  }, [employee]);

  const speichern = useMutation({
    mutationFn: ({ id, ziel }) => hrApi.setUserLink(id, ziel),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr', 'employees'] });
      onClose();
    },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function submit(e) {
    e.preventDefault();
    setError(null);
    speichern.mutate({ id: employee.id, ziel: userId || null });
  }

  /* Belegt heisst: gehört schon zu einer ANDEREN Akte. Das eigene bisherige
     Konto darf natürlich stehen bleiben. */
  const istBelegt = (id) => belegt?.has(id) && id !== employee?.userId;

  return (
    <Modal
      open={Boolean(employee)}
      onClose={onClose}
      title={t('hr.employees.link.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          {employee?.userId && (
            <Button
              variant="danger"
              onClick={() => speichern.mutate({ id: employee.id, ziel: null })}
              disabled={speichern.isPending}
            >
              {t('hr.employees.link.loesen')}
            </Button>
          )}
          <Button variant="primary" onClick={submit} disabled={speichern.isPending || !userId}>
            {speichern.isPending ? `${t('common.save')}…` : t('common.save')}
          </Button>
        </>
      }
    >
      {employee && (
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <p className="muted text-sm" style={{ margin: 0 }}>
            {t('hr.employees.link.hinweis', {
              name: `${employee.firstName} ${employee.lastName}`,
            })}
          </p>
          <label className="field">
            <span className="field-label">{t('hr.employees.link.konto')}</span>
            <select
              className="input"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              disabled={isLoading}
            >
              <option value="">{t('hr.employees.link.keins')}</option>
              {(konten || []).map((k) => (
                <option key={k.id} value={k.id} disabled={istBelegt(k.id)}>
                  {k.email}
                  {k.firstName || k.lastName ? ` (${[k.firstName, k.lastName].filter(Boolean).join(' ')})` : ''}
                  {istBelegt(k.id) ? ` - ${t('hr.employees.link.belegt')}` : ''}
                </option>
              ))}
            </select>
          </label>
          {ladeFehler && <div className="alert danger">{ladeFehler.message}</div>}
          {error && <div className="alert danger">{error}</div>}
        </form>
      )}
    </Modal>
  );
}
